<template>
 
  <div class="share-row" v-if="shareUrl">
    <a
      v-for="b in buttons"
      :key="b.network"
      class="social-share-button social-share-button--styled"
      :class="`social-share--${b.network}`"
      :style="{ '--color-brand': b.color }"
      :href="b.href"
      :target="b.scheme ? undefined : '_blank'"
      rel="noopener noreferrer"
      @click="b.scheme ? openScheme($event, b.href) : undefined"
      :aria-label="`Share on ${b.label}`"
    >
      <!-- ICON -->
      <i class="social-share-icon" :class="b.icon"></i>

      <!-- LABEL -->
      <span class="social-share-button__label">
        {{ b.label }}
      </span>
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRuntimeConfig } from '#imports'

type Props = {
  url?: string
  title?: string
  description?: string
}

const props = withDefaults(defineProps<Props>(), {
  url: '',
  title: '',
  description: ''
})

const route = useRoute()
const config = useRuntimeConfig()

// Naslov, opis i URL dolaze iz shareMeta store-a, a njega puni tek stranica, posle renderovanja
// layouta. Zato server renderuje bez njih, a klijent pri hidrataciji vec ima prave vrednosti
// (hydration mismatch). Prvi render je isti na serveru i klijentu (adresa stranice, bez teksta),
// a prave vrednosti iz propsa se uzimaju tek posle mount-a.
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})

const enc = (v: string) => encodeURIComponent(v || '')

const shareUrl = computed(() => {
  if (mounted.value && props.url?.startsWith('http')) return props.url
  return `${config.public.API_BASE}${route.fullPath}`
})

const shareText = computed(() =>
  mounted.value ? (props.description || props.title || '').trim() : ''
)
const truncate = (s, n = 120) => {
    s = String(s ?? '').trim()
    return s.length > n ? s.slice(0, n - 1) + '…' : s
  }

const cleanText = (t) => (t || '').replace(/https?:\/\/\S+/g, '').trim()

const buttons = computed(() => {
  const url = shareUrl.value
  const text = shareText.value

  return [
    {
      network: 'facebook',
      label: 'Facebook',
      color: '#1877F2',
      icon: 'esei icon-facebook-f-brands-solid',
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      scheme: false
    },
    {
      network: 'linkedin',
      label: 'LinkedIn',
      color: '#0A66C2',
      icon: 'esei icon-linkedin-in-brands-solid',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      scheme: false
    },
    {
      network: 'x',
      label: 'X',
      color: '#111111',
      icon: 'esei icon-x-twitter-brands-solid',
      href: `https://twitter.com/intent/tweet?text=${enc(truncate(text, 120))}&url=${enc(url)}`,
      scheme: false
    },
    {
      network: 'telegram',
      label: 'Telegram',
      color: '#229ED9',
      icon: 'esei icon-telegram-brands-solid',
      href: `https://t.me/share/url?url=${enc(url)}&text=${enc(truncate(text, 120))}`,
      scheme: false
    },
    {
      network: 'whatsapp',
      label: 'WhatsApp',
      color: '#25D366',
      icon: 'esei icon-whatsapp-brands-solid',
      href: `https://api.whatsapp.com/send?text=${enc(`${truncate(text, 120)} ${url}`.trim())}`,
      scheme: false
    },
    {
      network: 'viber',
      label: 'Viber',
      color: '#7360F2',
      icon: 'esei icon-viber-brands-solid',
      href: `viber://forward?text=${enc(`${truncate(cleanText(text), 120)}\n${url}`.trim())}`,
      scheme: true
    }
  ]
})

function openScheme(e: MouseEvent, href: string) {
  e.preventDefault()
  if (process.client) window.location.href = href
}
</script>

 